/**
 * Cliente HTTP compartido.
 *
 * Dos piezas importantes:
 *
 * 1. El access token vive en memoria (no en localStorage), así un XSS no puede
 *    leerlo desde el almacenamiento del navegador. La sesión se conserva entre
 *    recargas gracias a la cookie httpOnly de refresco.
 * 2. Un interceptor renueva el token al recibir un 401 y reintenta la petición
 *    una sola vez. Las peticiones que fallan mientras se renueva esperan a la
 *    misma promesa, de modo que N respuestas 401 simultáneas provocan un único
 *    refresh en vez de N.
 */

import axios, { type AxiosError, type AxiosInstance, type InternalAxiosRequestConfig } from 'axios';

import type { ApiErrorBody, TokenResponse } from '@/types/api';

const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '';

export const API_PREFIX = '/api/v1';

let accessToken: string | null = null;
let onSessionExpired: (() => void) | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export function getAccessToken(): string | null {
  return accessToken;
}

/** Registra qué hacer cuando la sesión ya no se puede renovar. */
export function setSessionExpiredHandler(handler: () => void): void {
  onSessionExpired = handler;
}

export const api: AxiosInstance = axios.create({
  baseURL: `${BASE_URL}${API_PREFIX}`,
  withCredentials: true, // imprescindible para la cookie de refresco
  headers: { 'Content-Type': 'application/json' },
  timeout: 20_000,
});

api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  if (accessToken) {
    config.headers.set('Authorization', `Bearer ${accessToken}`);
  }
  return config;
});

/** Endpoints donde un 401 es la respuesta legítima y no toca renovar nada. */
const NO_REFRESH_PATHS = ['/auth/login', '/auth/register', '/auth/refresh', '/auth/logout'];

let refreshPromise: Promise<string | null> | null = null;

async function refreshAccessToken(): Promise<string | null> {
  refreshPromise ??= (async () => {
    try {
      const { data } = await axios.post<TokenResponse>(
        `${BASE_URL}${API_PREFIX}/auth/refresh`,
        {},
        { withCredentials: true },
      );
      setAccessToken(data.access_token);
      return data.access_token;
    } catch {
      setAccessToken(null);
      onSessionExpired?.();
      return null;
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

type RetriableConfig = InternalAxiosRequestConfig & { _retried?: boolean };

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError<ApiErrorBody>) => {
    const config = error.config as RetriableConfig | undefined;
    const isAuthPath = NO_REFRESH_PATHS.some((path) => config?.url?.startsWith(path));

    if (error.response?.status === 401 && config && !config._retried && !isAuthPath) {
      config._retried = true;
      const token = await refreshAccessToken();
      if (token) {
        config.headers.set('Authorization', `Bearer ${token}`);
        return api.request(config);
      }
    }

    return Promise.reject(error);
  },
);

/** Error de la API ya normalizado, listo para mostrar al usuario. */
export class ApiError extends Error {
  readonly code: string;
  readonly status: number;
  readonly fieldErrors: Record<string, string>;

  constructor(message: string, code: string, status: number, fieldErrors: Record<string, string>) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.status = status;
    this.fieldErrors = fieldErrors;
  }
}

/** Convierte cualquier fallo de axios en un `ApiError` con mensaje legible. */
export function toApiError(error: unknown): ApiError {
  if (!axios.isAxiosError<ApiErrorBody>(error)) {
    return new ApiError('Ocurrió un error inesperado', 'unknown_error', 0, {});
  }

  if (!error.response) {
    return new ApiError(
      'No se pudo conectar con el servidor. Revisa tu conexión.',
      'network_error',
      0,
      {},
    );
  }

  const body = error.response.data;
  const fieldErrors = Object.fromEntries(
    (body?.errors ?? []).map((item) => [item.field, item.message]),
  );

  return new ApiError(
    body?.detail ?? 'Ocurrió un error inesperado',
    body?.code ?? 'unknown_error',
    error.response.status,
    fieldErrors,
  );
}

/** Ruta pública de un archivo subido (audio o imagen). */
export function mediaUrl(folder: 'audio' | 'images', filename: string | null): string | null {
  return filename ? `${BASE_URL}/media/${folder}/${filename}` : null;
}
